import localStorage from 'LocalStorage';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import ContentWorkers from 'notebook/workers/ContentWorkers';
import EntityStore from 'stores/EntityStore';
import APIUtil from 'util/APIUtil';
import * as logging from 'util/Logging';
import * as net from 'util/Net';

export interface LocalDraft<TItem> {
  timestamp: Date;
  item?: TItem;
  cidIdMap?: CidIdMap;
  unsaved?: boolean;
  lockedFromExternalChange?: boolean;
  author?: string;
  dismissed?: boolean;
  recovered?: boolean;
}

const DRAFT_TTL_MS = 3 * 24 * 60 * 60 * 1000;
export const DRAFTS_KEY = 'localDrafts-v2';
export const RECOVERY_HASH = '#recover';
export const RECOVERY_ID = 'recovery';

export function getLocalDrafts(): Record<string, LocalDraft<unknown>> {
  const drafts = (localStorage.json.getItem(DRAFTS_KEY) as Record<string, LocalDraft<unknown>>) ?? {};
  // Convert every serialized timestamp string to an actual Date object.
  for (const [draftId, draft] of Object.entries(drafts)) {
    // In practice this is mostly a moot check, but helps catch stray drafts that may have been written without a timestamp
    // declared (in tests, or in some other malformed route).
    if (draft.timestamp != null) {
      drafts[draftId] = {...draft, timestamp: new Date(draft.timestamp)};
    }
  }
  return drafts;
}

export function setLocalDrafts(drafts: Record<string, LocalDraft<unknown>>): void {
  ContentWorkers.stringify(drafts).then((json) => {
    localStorage.setItem(DRAFTS_KEY, json);
  });
}

export function postClientDump<TOptions extends {status: number | null}>(
  extra: TOptions,
  message: string
): void {
  if (extra.status && extra.status < 500) {
    logging.dump('postClientDump', `Failed to save draft [${extra.status}] ${message}`, {
      data: {localDrafts: getLocalDrafts()},
      extra,
    });
  }
}

export async function requestRecovery(): Promise<void> {
  await net.postJSONPromise({
    url: '/1/api/debug/request-recovery',
    data: {
      localDrafts: getLocalDrafts(),
    },
  });
}

export const getDraftId = (prefix: PropertyKey, id: string): string => `${String(prefix)}-${id}`;
export const apiIdFromDraftId = (draftId: string): string => draftId.split('-')[1];

function _hasStringId(value: unknown): value is {id: string} {
  return typeof value === 'object' && value != null && typeof (value as {id: string}).id === 'string';
}

export function isRecoverable<TItem>(draft?: LocalDraft<TItem>): boolean {
  if (draft == null || !draft.unsaved) {
    return false;
  }
  if (draft.author == null || draft.author !== EntityStore.currentUser().id) {
    return false;
  }
  // Do a runtime check for this since we can't actually know what's provided to this function.
  return _hasStringId(draft.item) && APIUtil.isEntry(draft.item.id);
}

export function shouldPromptForRecovery<TItem>(draft: LocalDraft<TItem>): boolean {
  return isRecoverable(draft) && !draft.recovered && !draft.dismissed && !draft.lockedFromExternalChange;
}

export function recoverableDataExist(): boolean {
  return Object.values(getLocalDrafts()).some((draft: LocalDraft<unknown>) => shouldPromptForRecovery(draft));
}

export function removeDraft(draftId: string): void {
  const localDrafts = getLocalDrafts();
  delete localDrafts[draftId];
  setLocalDrafts(localDrafts);
}

export function initDrafts(): void {
  // TODO(T###, 2016-07-15): The data stored in 'localDrafts' is not accurate due to bug T5729, so we've
  // switched to a new localStorage key: 'localDrafts-v2'. To avoid keeping around old data (which eats into
  // our 5MB), we immediately clear it.
  localStorage.removeItem('localDrafts');

  const localDrafts = getLocalDrafts();
  for (const draftId of Object.keys(localDrafts)) {
    const draft = localDrafts[draftId];
    // 3 day window
    if (Date.now() - draft.timestamp.getTime() > DRAFT_TTL_MS) {
      delete localDrafts[draftId];
    }
  }
  setLocalDrafts(localDrafts);
}
